import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Wrench, 
  Settings, 
  Shield, 
  Archive, 
  Sparkles, 
  RotateCcw,
  Zap,
  Target,
  ArrowRight
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { trackServiceInteraction } from '../../lib/analytics';

interface ServiceCard {
  id: string;
  icon: React.ElementType;
  title: string;
  description: string;
  features: string[];
  link: string;
  gradient: string;
}

const ServicesGrid: React.FC = () => {
  const { t, translations } = useLanguage();
  const [flippedCard, setFlippedCard] = useState<string | null>(null);

  const services: ServiceCard[] = [
    {
      id: 'standard',
      icon: Wrench,
      title: t('home.services.standard.title'),
      description: t('home.services.standard.description'),
      features: translations.home?.services?.standard?.items || [],
      link: '/services#standard',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      id: 'premium',
      icon: Target,
      title: t('home.services.premium.title'),
      description: t('home.services.premium.description'),
      features: translations.home?.services?.premium?.items || [],
      link: '/services#premium',
      gradient: 'from-red-500 to-red-600'
    },
    {
      id: 'geometry',
      icon: Settings,
      title: t('home.services.geometry.title'),
      description: t('home.services.geometry.description'),
      features: translations.home?.services?.geometry?.items || [],
      link: '/services#geometry',
      gradient: 'from-green-500 to-green-600'
    },
    {
      id: 'storage',
      icon: Archive,
      title: t('home.services.storage.title'),
      description: t('home.services.storage.description'),
      features: translations.home?.services?.storage?.items || [],
      link: '/services#storage',
      gradient: 'from-purple-500 to-purple-600'
    },
    {
      id: 'repair',
      icon: Shield,
      title: t('home.services.repair.title'),
      description: t('home.services.repair.description'),
      features: translations.home?.services?.repair?.items || [],
      link: '/services#repair',
      gradient: 'from-orange-500 to-orange-600'
    },
    {
      id: 'cleaning',
      icon: Sparkles,
      title: t('home.services.cleaning.title'),
      description: t('home.services.cleaning.description'),
      features: translations.home?.services?.cleaning?.items || [],
      link: '/services#cleaning',
      gradient: 'from-cyan-500 to-cyan-600'
    }
  ];

  const handleCardHover = (cardId: string) => {
    setFlippedCard(cardId);
    trackServiceInteraction(cardId, 'card_hover');
  };

  const handleCardLeave = () => {
    setFlippedCard(null);
  };

  const handleCardClick = (cardId: string) => {
    setFlippedCard(flippedCard === cardId ? null : cardId);
    trackServiceInteraction(cardId, 'card_click');
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('home.services.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('home.services.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            const isFlipped = flippedCard === service.id;
            
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative h-80 perspective-1000"
                onMouseEnter={() => handleCardHover(service.id)}
                onMouseLeave={handleCardLeave}
                onClick={() => handleCardClick(service.id)}
              >
                {/* Card Container */}
                <div 
                  className={`relative w-full h-full duration-500 preserve-3d cursor-pointer ${
                    isFlipped ? 'rotate-y-180' : ''
                  }`}
                  style={{
                    transformStyle: 'preserve-3d',
                    transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  {/* Front Face */}
                  <div 
                    className="absolute inset-0 w-full h-full backface-hidden rounded-2xl bg-white shadow-lg hover:shadow-xl transition-shadow duration-300"
                    style={{ backfaceVisibility: 'hidden' }}
                  >
                    <div className="p-8 h-full flex flex-col items-center justify-center text-center">
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${service.gradient} flex items-center justify-center mb-6 shadow-lg`}
                      >
                        <IconComponent className="w-8 h-8 text-white" />
                      </motion.div>
                      
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {service.title}
                      </h3>
                      
                      <p className="text-gray-600 leading-relaxed">
                        {service.description}
                      </p>

                      <motion.div
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        className="mt-6 text-gray-400"
                      >
                        <RotateCcw className="w-6 h-6" />
                      </motion.div>
                    </div>
                  </div>

                  {/* Back Face */}
                  <div 
                    className={`absolute inset-0 w-full h-full backface-hidden rotate-y-180 rounded-2xl bg-gradient-to-br ${service.gradient} text-white shadow-xl`}
                    style={{ backfaceVisibility: 'hidden' }}
                  >
                    <div className="p-8 h-full flex flex-col justify-between">
                      <div>
                        <h3 className="text-2xl font-bold mb-6">{service.title}</h3>
                        <ul className="space-y-3">
                          {service.features.map((feature, featureIndex) => (
                            <motion.li
                              key={featureIndex}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: isFlipped ? 1 : 0, x: isFlipped ? 0 : -20 }}
                              transition={{ delay: featureIndex * 0.1 }}
                              className="flex items-center space-x-3"
                            >
                              <Zap className="w-4 h-4 text-yellow-300 flex-shrink-0" />
                              <span className="text-white/90">{feature}</span>
                            </motion.li>
                          ))}
                        </ul>
                      </div>

                      <motion.a
                        href={service.link}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="inline-flex items-center justify-center space-x-2 mt-6 px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-all duration-200"
                      >
                        <span className="font-medium">{t('common.cta.learnMore')}</span>
                        <ArrowRight className="w-4 h-4" />
                      </motion.a>
                    </div>
                  </div>
                </div>

                {/* Mobile Back Button (only visible on mobile when flipped) */}
                {isFlipped && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFlippedCard(null);
                    }}
                    className="md:hidden absolute top-4 right-4 z-10 p-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-all duration-200"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-16"
        >
          <motion.a
            href="/services"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center space-x-3 px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <span>{t('common.cta.viewServices')}</span>
            <motion.div
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <ArrowRight className="w-5 h-5" />
            </motion.div>
          </motion.a>
        </motion.div>
      </div>

      <style>{`
        .perspective-1000 {
          perspective: 1000px;
        }
        .preserve-3d {
          transform-style: preserve-3d;
        }
        .backface-hidden {
          backface-visibility: hidden;
        }
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
      `}</style>
    </section>
  );
};

export default ServicesGrid;